import {
  BadgePercent,
  BarChart3,
  Boxes,
  Calendar,
  ChevronRight,
  Clock,
  CreditCard,
  ExternalLink,
  Headphones,
  LayoutDashboard,
  LogOut,
  Megaphone,
  Menu,
  Package,
  RotateCcw,
  Search,
  ShieldCheck,
  ShoppingBag,
  Store,
  UserCog,
  Wallet,
  X,
  type LucideIcon,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Link, NavLink, Navigate, Outlet, useLocation } from 'react-router-dom';

import { ConfirmDialog, Spinner } from '@terra/ui';

import { STOREFRONT_URL } from '../lib/appUrls';
import { isVendor, useAuthStore } from '../stores/authStore';

import { NotificationBell } from './NotificationBell';
import { StoreSuspendedBanner } from './StoreSuspendedBanner';

// ── Navigation model ─────────────────────────────────────────────────────────

interface NavLeaf {
  to: string;
  label: string;
  icon: LucideIcon;
  end?: boolean;
}
interface NavSection {
  title: string;
  items: NavLeaf[];
}

const NAV: NavSection[] = [
  {
    title: 'Overview',
    items: [{ to: '/', label: 'Overview', icon: LayoutDashboard, end: true }],
  },
  {
    title: 'Store',
    items: [
      { to: '/onboarding', label: 'Onboarding & KYC', icon: ShieldCheck },
      { to: '/account', label: 'My account', icon: UserCog },
    ],
  },
  {
    title: 'Catalog',
    items: [
      { to: '/products', label: 'Products', icon: Package },
      { to: '/inventory', label: 'Inventory', icon: Boxes },
    ],
  },
  {
    title: 'Sales',
    items: [
      { to: '/orders', label: 'Orders', icon: ShoppingBag },
      { to: '/returns', label: 'Returns', icon: RotateCcw },
    ],
  },
  {
    title: 'Finance',
    items: [
      { to: '/wallet', label: 'Wallet & payouts', icon: Wallet },
      { to: '/subscription', label: 'Subscription', icon: CreditCard },
    ],
  },
  {
    title: 'Growth',
    items: [
      { to: '/offers', label: 'Offers', icon: BadgePercent },
      { to: '/sponsored', label: 'Sponsored ads', icon: Megaphone },
      { to: '/analytics', label: 'Analytics', icon: BarChart3 },
    ],
  },
  {
    title: 'Support',
    items: [
      { to: '/customer-support', label: 'Customer Support', icon: Headphones },
      { to: '/support', label: 'Platform Support', icon: Headphones },
      { to: '/product-support', label: 'Customer Q&A', icon: Headphones },
      { to: '/notes', label: 'Notes & Policies', icon: ShieldCheck },
    ],
  },
];

const ALL_ITEMS = NAV.flatMap((s) => s.items);

/** Resolve the page title for the current route. */
function resolveTitle(path: string): string {
  if (path.startsWith('/products/')) return 'Products';
  return ALL_ITEMS.find((i) => i.to === path)?.label ?? 'Seller';
}

/** Resolve which section a route belongs to (for the breadcrumb). */
function resolveSection(path: string): string {
  const base = path.startsWith('/products/') ? '/products' : path;
  return NAV.find((s) => s.items.some((i) => i.to === base))?.title ?? 'Overview';
}

// ── Global toaster ───────────────────────────────────────────────────────────
//
// Listens for `terra:notify` CustomEvents (dispatched from the queryClient's
// default mutation onError) and renders a transient toast. Keeping the toaster
// inside VendorShell means unauthenticated routes (login/register) don't show
// shell-scoped errors, but every authenticated mutation gets visible feedback.

interface ToastMsg {
  id: number;
  kind: 'error' | 'success' | 'info';
  message: string;
}

function Toaster() {
  const [toasts, setToasts] = useState<ToastMsg[]>([]);
  useEffect(() => {
    let nextId = 1;
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{ kind?: ToastMsg['kind']; message?: string }>).detail;
      if (!detail?.message) return;
      const toast: ToastMsg = {
        id: nextId++,
        kind: detail.kind ?? 'info',
        message: detail.message,
      };
      setToasts((prev) => [...prev, toast]);
      // Auto-dismiss after 5 seconds — long enough to read, short enough not
      // to linger if another error is incoming.
      setTimeout(() => {
        setToasts((prev) => prev.filter((t) => t.id !== toast.id));
      }, 5000);
    };
    window.addEventListener('terra:notify', handler);
    return () => window.removeEventListener('terra:notify', handler);
  }, []);

  if (toasts.length === 0) return null;
  return (
    <div className="pointer-events-none fixed bottom-4 right-4 z-50 flex max-w-sm flex-col gap-2">
      {toasts.map((t) => (
        <div
          key={t.id}
          role="status"
          className={`pointer-events-auto rounded-xl border px-4 py-3 text-sm font-semibold shadow-lg backdrop-blur ${
            t.kind === 'error'
              ? 'border-rose-200 bg-rose-50/95 text-rose-800'
              : t.kind === 'success'
                ? 'border-emerald-200 bg-emerald-50/95 text-emerald-800'
                : 'border-slate-200 bg-white/95 text-slate-700'
          }`}
        >
          {t.message}
        </div>
      ))}
    </div>
  );
}

// ── Header live Date & Time widget ───────────────────────────────────────────

function LiveDateTimeWidget() {
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const timer = setInterval(() => {
      setNow(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const timeString = now.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  });

  const dateString = now.toLocaleDateString('en-US', {
    weekday: 'short',
    day: '2-digit',
    month: 'short',
  });

  const yearString = now.getFullYear();

  return (
    <div className="hidden items-center gap-2 sm:flex">
      <div className="border-border/70 bg-surface/90 hover:border-gold-500/40 shadow-zone-sm flex items-center gap-2 rounded-xl border px-3 py-1.5 text-xs backdrop-blur-md transition-all duration-200">
        {/* Live Pulse Indicator */}
        <span className="relative flex h-2 w-2 shrink-0" title="Live Clock">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
          <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
        </span>

        {/* Date (visible on md+) */}
        <div className="border-border/60 text-ink-muted hidden items-center gap-1.5 border-r pr-2.5 font-medium md:flex">
          <Calendar className="text-gold-600 h-3.5 w-3.5 shrink-0" />
          <span>
            {dateString}, {yearString}
          </span>
        </div>

        {/* Time */}
        <div className="text-ink flex items-center gap-1.5 font-semibold tabular-nums">
          <Clock className="text-gold-600 h-3.5 w-3.5 shrink-0" />
          <span>{timeString}</span>
        </div>
      </div>
    </div>
  );
}

// ── Header Storefront Link Button ────────────────────────────────────────────

function StorefrontLinkButton() {
  return (
    <a
      href={STOREFRONT_URL}
      target="_blank"
      rel="noopener noreferrer"
      title="Open storefront in new tab to view live updates"
      className="via-gold-500 hover:via-gold-400 group relative inline-flex items-center gap-2 overflow-hidden rounded-xl bg-gradient-to-r from-amber-500 to-amber-600 px-3.5 py-2 text-xs font-bold text-white shadow-md shadow-amber-500/20 transition-all duration-300 hover:scale-[1.02] hover:from-amber-400 hover:to-amber-500 hover:shadow-lg hover:shadow-amber-500/35 active:scale-[0.98]"
    >
      {/* Animated Light Reflection Effect on Hover */}
      <span className="pointer-events-none absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/30 to-transparent transition-transform duration-700 ease-out group-hover:translate-x-full" />

      <Store className="h-4 w-4 shrink-0 transition-transform duration-300 group-hover:scale-110" />
      <span className="hidden whitespace-nowrap font-bold tracking-wide sm:inline">
        Visit Storefront
      </span>
      <span className="inline sm:hidden">Store</span>
      <ExternalLink className="h-3.5 w-3.5 shrink-0 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
    </a>
  );
}

// ── Signed-in seller's picture ─────────────────────────────────────────────

function Avatar({ url, initial }: { url: string | null | undefined; initial: string }) {
  return (
    <div className="grid h-9 w-9 shrink-0 place-items-center overflow-hidden rounded-lg bg-red-500 text-sm font-bold text-white">
      {url ? <img src={url} alt="" className="h-full w-full object-cover" /> : initial}
    </div>
  );
}

// ── Shell ────────────────────────────────────────────────────────────────────

export function VendorShell() {
  const { user, status, logout } = useAuthStore();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [query, setQuery] = useState('');

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const pageTitle = useMemo(() => resolveTitle(location.pathname), [location.pathname]);
  const section = useMemo(() => resolveSection(location.pathname), [location.pathname]);

  // Sign-out asks first, in the app's own dialog rather than the browser's.
  const [confirmLogout, setConfirmLogout] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const handleLogout = () => setConfirmLogout(true);
  const doLogout = async () => {
    setLoggingOut(true);
    try {
      await logout();
    } finally {
      setLoggingOut(false);
      setConfirmLogout(false);
    }
  };

  // Search-filtered nav.
  const q = query.trim().toLowerCase();
  const sections = useMemo(() => {
    if (!q) return NAV;
    return NAV.map((s) => ({
      ...s,
      items: s.items.filter((i) => i.label.toLowerCase().includes(q)),
    })).filter((s) => s.items.length > 0);
  }, [q]);

  if (status === 'idle' || status === 'loading') {
    return (
      <div className="bg-surface-subtle grid min-h-screen place-items-center">
        <Spinner size="lg" />
      </div>
    );
  }
  if (status === 'anonymous' || !isVendor(user)) {
    return <Navigate to="/login" replace />;
  }

  const initial = (user?.name ?? user?.email ?? 'S').slice(0, 1).toUpperCase();
  const email = user?.email ?? '';
  const trimmedEmail = email.length > 24 ? `${email.slice(0, 21)}…` : email;

  const sidebar = (
    <div className="bg-zone-900 flex h-full flex-col">
      {/* Logo */}
      <div className="relative flex items-center justify-center px-5 pb-3 pt-6">
        <img src="/image.png" alt="Terra Shoppy" className="h-22 w-60 object-contain" />
        <button
          type="button"
          onClick={() => setMobileOpen(false)}
          className="text-zone-400 absolute right-3 top-4 grid h-9 w-9 place-items-center rounded-lg transition-colors hover:bg-white/5 hover:text-white lg:hidden"
          aria-label="Close menu"
        >
          <X className="h-[18px] w-[18px]" />
        </button>
      </div>

      {/* Search */}
      <div className="px-3 pb-2 pt-1">
        <div className="bg-zone-700/60 focus-within:border-gold-500/40 focus-within:bg-zone-700 flex items-center gap-2 rounded-xl border border-white/5 px-3.5 transition-colors duration-200">
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search in menu"
            aria-label="Search in menu"
            className="placeholder:text-zone-400 h-10 w-full bg-transparent text-sm text-white outline-none"
          />
          <Search className="text-zone-400 h-4 w-4 shrink-0" strokeWidth={2} />
        </div>
      </div>

      {/* Navigation */}
      <nav className="ts-scroll-dark flex-1 space-y-5 overflow-y-auto px-3 py-3">
        {sections.length === 0 ? (
          <p className="text-zone-400 px-3 py-8 text-center text-sm">No menu items found.</p>
        ) : (
          sections.map((sec) => (
            <div key={sec.title}>
              <p className="text-zone-500 px-3 pb-1.5 text-[10px] font-bold uppercase tracking-[0.16em]">
                {sec.title}
              </p>
              <div className="space-y-0.5">
                {sec.items.map((entry) => (
                  <NavLink
                    key={entry.to}
                    to={entry.to}
                    end={entry.end ?? false}
                    className={({ isActive }) =>
                      `group relative flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm no-underline transition-all duration-200 ${
                        isActive
                          ? 'bg-white/[0.06] font-semibold text-white'
                          : 'text-zone-300 font-medium hover:bg-white/[0.04] hover:text-white'
                      }`
                    }
                  >
                    {({ isActive }) => (
                      <>
                        <span
                          className={`bg-gold-500 absolute left-0 top-1/2 h-5 w-[3px] -translate-y-1/2 rounded-r-full transition-opacity duration-200 ${
                            isActive ? 'opacity-100' : 'opacity-0'
                          }`}
                        />
                        <entry.icon
                          className={`h-[19px] w-[19px] shrink-0 transition-colors duration-200 ${
                            isActive ? 'text-gold-400' : 'text-zone-400 group-hover:text-white'
                          }`}
                          strokeWidth={1.9}
                          aria-hidden
                        />
                        <span className="flex-1 truncate">{entry.label}</span>
                      </>
                    )}
                  </NavLink>
                ))}
              </div>
            </div>
          ))
        )}
      </nav>

      {/* Footer — seller identity */}
      <div className="border-zone-600 mx-3 mb-3 mt-1 flex items-center gap-2.5 rounded-xl border bg-white/[0.03] p-2.5">
        <Link to="/account" className="flex min-w-0 flex-1 items-center gap-2.5" title="My account">
          <Avatar url={user?.avatarUrl} initial={initial} />
          <div className="min-w-0 flex-1 leading-tight">
            <p className="truncate text-xs font-semibold text-white">{user?.name ?? 'Seller'}</p>
            <p className="text-zone-400 truncate text-[11px]">{trimmedEmail}</p>
          </div>
        </Link>
        <button
          type="button"
          onClick={handleLogout}
          className="text-zone-400 grid h-8 w-8 shrink-0 place-items-center rounded-lg transition-colors hover:bg-white/5 hover:text-white"
          aria-label="Sign out"
          title="Sign out"
        >
          <LogOut className="h-[17px] w-[17px]" />
        </button>
      </div>
    </div>
  );

  return (
    <div className="bg-surface-subtle min-h-screen">
      <aside className="shadow-zone fixed inset-y-0 left-0 z-30 hidden w-[264px] lg:block">
        {sidebar}
      </aside>

      {mobileOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div
            className="bg-zone-950/70 absolute inset-0 backdrop-blur-sm"
            onClick={() => setMobileOpen(false)}
            aria-hidden
          />
          <aside className="shadow-zone-lift absolute inset-y-0 left-0 w-[284px]">{sidebar}</aside>
        </div>
      )}

      <div className="lg:pl-[264px]">
        <header className="border-border bg-surface/85 sticky top-0 z-20 flex h-[72px] items-center gap-4 border-b px-4 backdrop-blur-xl md:px-7">
          <button
            type="button"
            onClick={() => setMobileOpen(true)}
            className="border-border text-ink-muted hover:bg-surface-subtle grid h-10 w-10 place-items-center rounded-xl border transition-colors lg:hidden"
            aria-label="Open menu"
          >
            <Menu className="h-[18px] w-[18px]" />
          </button>

          <div className="min-w-0">
            <p className="text-ink-subtle flex items-center gap-1.5 text-[11px] font-semibold">
              <span className="text-gold-600">{section}</span>
              <ChevronRight className="h-3 w-3" />
              <span className="text-ink-muted">{pageTitle}</span>
            </p>
            <h1 className="text-ink truncate text-lg font-bold leading-tight tracking-tight">
              {pageTitle}
            </h1>
          </div>

          <div className="ml-auto flex items-center gap-2.5 md:gap-3">
            <LiveDateTimeWidget />
            <StorefrontLinkButton />
            <NotificationBell />
            <div className="border-border flex items-center gap-2 rounded-xl border py-1.5 pl-1.5 pr-1.5">
              <Link to="/account" className="flex items-center gap-2" title="My account">
                <Avatar url={user?.avatarUrl} initial={initial} />
                <div className="hidden min-w-0 leading-tight sm:block">
                  <p className="text-ink max-w-[160px] truncate text-xs font-semibold">
                    {user?.name ?? 'Seller'}
                  </p>
                  <p className="text-ink-muted max-w-[160px] truncate text-[11px]">
                    {trimmedEmail}
                  </p>
                </div>
              </Link>
              <span className="bg-border mx-0.5 hidden h-7 w-px sm:block" />
              <button
                type="button"
                onClick={handleLogout}
                className="text-ink-muted grid h-9 w-9 shrink-0 place-items-center rounded-lg transition-all duration-200 hover:bg-red-50 hover:text-red-600"
                aria-label="Sign out"
                title="Sign out"
              >
                <LogOut className="h-[18px] w-[18px]" />
              </button>
            </div>
          </div>
        </header>

        <main className="ts-scroll min-h-[calc(100vh-72px)] overflow-x-hidden p-4 md:p-6 lg:p-8">
          <div className="mx-auto max-w-screen-2xl">
            <StoreSuspendedBanner />
            <Outlet />
          </div>
        </main>
      </div>

      <Toaster />

      <ConfirmDialog
        open={confirmLogout}
        tone="danger"
        icon={<LogOut className="h-6 w-6" />}
        title="Log out?"
        message="Are you sure you want to log out of your account? You'll need to sign in again to continue."
        confirmLabel="Log out"
        cancelLabel="Stay signed in"
        loading={loggingOut}
        onConfirm={() => void doLogout()}
        onCancel={() => setConfirmLogout(false)}
      />
    </div>
  );
}
